import useSWR from "swr"
import { api_fetch_throws } from "@/api/client"
import { User } from "@/api/user"

/** PURE: how a user is shown in an owner or approver picker — full name, else the email. */
export function user_display_name(user: Pick<User, "first_name" | "last_name" | "email">): string {
  const name = [user.first_name, user.last_name].filter(Boolean).join(" ").trim()
  return name || user.email
}

/** PURE: select options for a user picker, the current user first and marked "(you)". */
export function user_select_options(users: Array<Pick<User, "id" | "first_name" | "last_name" | "email">>, current_user_id?: string) {
  return users
    .map((user) => ({ value: user.id, label: user.id === current_user_id ? `${user_display_name(user)} (you)` : user_display_name(user) }))
    .sort((a, b) => (a.value === current_user_id ? -1 : b.value === current_user_id ? 1 : a.label.localeCompare(b.label)))
}

/** PURE: the display name for a user id, or null when the id is unknown or blank. */
export function user_name_by_id(users: Array<Pick<User, "id" | "first_name" | "last_name" | "email">> | undefined, id: string | undefined | null): string | null {
  if (!id || !users) return null
  const user = users.find((candidate) => candidate.id === id)
  return user ? user_display_name(user) : null
}

async function fetch_org_users(): Promise<User[]> {
  return api_fetch_throws<User[]>("/users", { use_cache: true })
}

/** The org's assignable users (owners and approvers), cached across the forms that need them. */
export function use_org_users(): User[] | undefined {
  const { data } = useSWR("/users", fetch_org_users, { revalidateOnFocus: false })
  return data
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest
  const users = [
    { id: "u1", first_name: "Bakhrom", last_name: "Abdurakhmonov", email: "bakhrom@example.com" },
    { id: "u2", first_name: null, last_name: null, email: "ap@example.com" },
    { id: "u3", first_name: "Tyler", last_name: null, email: "tyler@example.com" },
  ]

  describe("user_display_name", () => {
    it("uses the full name, falling back to the email", () => {
      expect(user_display_name(users[0])).toBe("Bakhrom Abdurakhmonov")
      expect(user_display_name(users[1])).toBe("ap@example.com")
      expect(user_display_name(users[2])).toBe("Tyler")
    })
  })

  describe("user_select_options", () => {
    it("puts the current user first and marks them", () => {
      expect(user_select_options(users, "u3").map((option) => option.label)).toEqual(["Tyler (you)", "ap@example.com", "Bakhrom Abdurakhmonov"])
    })
  })

  describe("user_name_by_id", () => {
    it("names a known id and gives null otherwise", () => {
      expect(user_name_by_id(users, "u1")).toBe("Bakhrom Abdurakhmonov")
      expect(user_name_by_id(users, "nope")).toBeNull()
      expect(user_name_by_id(undefined, "u1")).toBeNull()
    })
  })
}
